"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// @owlcat/app-shell — in-app purchase widgets: <SubscribeWidget> (buy a subscription)
// and <BuyTokensWidget> (top up wallet tokens), BOTH without the redirect-to-accounts
// bounce.
//
// ── Context gating (HARD compliance rule — Telegram ToS on digital goods) ────────
//   • Inside a Telegram Mini App → digital goods may be paid ONLY with Telegram Stars.
//     The Stripe/card path is NEVER rendered (paymentMethods() forces card=false when
//     inTma). Rendering card in a TMA risks a Telegram ban.
//   • In a plain browser → "Pay with card" opens Stripe EMBEDDED Checkout in an in-app
//     MODAL (no navigation away) via @stripe/react-stripe-js, PLUS "Pay with Telegram
//     Stars" (a t.me invoice deep-link opened in a new tab).
//
// ── Completion ───────────────────────────────────────────────────────────────────
// The purchase is confirmed ASYNC by the accounts webhook (embedded checkout uses
// redirect_on_completion:'never'; Stars credits arrive via the bot webhook). On the
// client's completion signal (Stripe onComplete / Stars return) we POLL the SELF
// endpoint until the new tier / higher balance appears, then close + show success. A
// timeout degrades gracefully to a "processing, may take a moment" hint.
//
// `accountsUrl` (NEXT_PUBLIC_ACCOUNTS_URL) + `stripePublishableKey`
// (NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY) are passed as PROPS — a NEXT_PUBLIC var must be
// referenced literally in the app to be inlined (mirrors SupportWidget/LegalFooter).
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, } from "@owlcat/ui";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { isTelegramMiniApp, openTelegramInvoice } from "./telegram";
import { useTrack } from "./analytics";
import { formatPrice, paymentMethods, pickDisplayCurrency, entitlementHasTier, balanceIncreased, packShareOfMonth, planCtaState, } from "./purchase-logic";
// ── openers (event-driven, like openSupportWidget / openConsentBanner) ───────────
/** Window event any app chrome can dispatch to open the shared <SubscribeWidget>. */
export const SUBSCRIBE_OPEN_EVENT = "owlcat:open-subscribe";
/** Window event any app chrome can dispatch to open the shared <BuyTokensWidget>. */
export const BUY_TOKENS_OPEN_EVENT = "owlcat:open-tokens";
/** Window event the widgets dispatch once a purchase has LANDED (poll confirmed):
 *  detail { appId, mode } — apps re-read their quota/entitlement on it. */
export const PURCHASE_COMPLETE_EVENT = "owlcat:purchase-complete";
/** Open the shared <SubscribeWidget> from anywhere (e.g. a header "Upgrade" button).
 *  `tier` (optional) = the plan the visitor already chose (a landing's plans table → sign-in →
 *  `?subscribe=<tier>`): its card is highlighted and scrolled into view. */
export function openSubscribe(appId, tier) {
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(SUBSCRIBE_OPEN_EVENT, { detail: { appId, tier } }));
    }
}
/** Open the shared <BuyTokensWidget> from anywhere (e.g. a low-balance nudge). */
export function openBuyTokens(appId) {
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(BUY_TOKENS_OPEN_EVENT, { detail: { appId } }));
    }
}
// ── mount registry (for graceful-fallback callers) ───────────────────────────────
// A mounted <SubscribeWidget>/<BuyTokensWidget> bumps a window counter; chrome that
// wants to open it in-app (header chip, "Upgrade" menu item) can check first and, if
// the widget isn't mounted (JS disabled, old chunk, app didn't mount it), fall back to
// the redirect link instead of firing an event nothing listens to.
const SUB_FLAG = "__owlcatSubscribeMounted";
const TOK_FLAG = "__owlcatBuyTokensMounted";
function bumpMountFlag(flag, delta) {
    if (typeof window === "undefined")
        return;
    const w = window;
    w[flag] = Math.max(0, (w[flag] ?? 0) + delta);
}
/** True when a <SubscribeWidget> is mounted and listening for openSubscribe(). */
export function isSubscribeWidgetMounted() {
    return typeof window !== "undefined" && (window[SUB_FLAG] ?? 0) > 0;
}
/** True when a <BuyTokensWidget> is mounted and listening for openBuyTokens(). */
export function isBuyTokensWidgetMounted() {
    return typeof window !== "undefined" && (window[TOK_FLAG] ?? 0) > 0;
}
const DEFAULT_LABELS = {
    subscribeTitle: "Choose a plan",
    subscribeDescription: "Upgrade to unlock more — pay securely without leaving the app.",
    tokensTitle: "Top up",
    tokensDescription: "Choose a pack — pay securely without leaving the app.",
    payWithCard: "Pay with card",
    payWithStars: "Pay with Telegram Stars",
    loading: "Loading…",
    starsWaitingHint: "Complete the payment in Telegram — your purchase will appear here shortly.",
    processingHint: "Payment received — it may take a moment to activate.",
    success: "Done! Your purchase is active.",
    error: "Something went wrong. Please try again.",
    close: "Close",
    emptyCatalog: "No plans are available right now.",
    currentBadge: "Current",
    manage: "Manage",
    switchPlan: "Switch",
    tabSubscription: "Subscription",
    tabTopUp: "Top up",
    packOfMonth: "+{pct}% to the month’s allowance",
};
// Built-in translations for the widget's OWN UI strings, keyed by 2-letter lang.
// Applied by `loc` (the `locale` prop, else navigator.language) so the widget is
// localized even when the host app passes no `labels`. Plan/pack FEATURE bullets
// come from catalog metadata and are NOT translated here. Caller `labels` still
// win (spread last). English lives in DEFAULT_LABELS.
const WIDGET_LOCALE_LABELS = {
    ru: {
        subscribeTitle: "Выберите план",
        subscribeDescription: "Оформите подписку — оплата безопасно, не выходя из приложения.",
        tokensTitle: "Пополнить",
        tokensDescription: "Выберите пакет — оплата безопасно, не выходя из приложения.",
        payWithCard: "Оплатить картой",
        payWithStars: "Оплатить Telegram Stars",
        loading: "Загрузка…",
        starsWaitingHint: "Завершите оплату в Telegram — покупка появится здесь через мгновение.",
        processingHint: "Оплата получена — активация займёт немного времени.",
        success: "Готово! Покупка активна.",
        error: "Что-то пошло не так. Попробуйте ещё раз.",
        close: "Закрыть",
        emptyCatalog: "Сейчас нет доступных планов.",
        currentBadge: "Текущий",
        manage: "Управление",
        switchPlan: "Перейти",
        tabSubscription: "Подписка",
        tabTopUp: "Пополнение",
        packOfMonth: "+{pct}% к лимиту месяца",
    },
    cs: {
        subscribeTitle: "Vyberte plán",
        subscribeDescription: "Předplaťte si — bezpečná platba bez opuštění aplikace.",
        tokensTitle: "Dobít",
        tokensDescription: "Vyberte balíček — bezpečná platba bez opuštění aplikace.",
        payWithCard: "Zaplatit kartou",
        payWithStars: "Zaplatit Telegram Stars",
        loading: "Načítání…",
        starsWaitingHint: "Dokončete platbu v Telegramu — nákup se zde brzy objeví.",
        processingHint: "Platba přijata — aktivace může chvíli trvat.",
        success: "Hotovo! Nákup je aktivní.",
        error: "Něco se pokazilo. Zkuste to znovu.",
        close: "Zavřít",
        emptyCatalog: "Momentálně nejsou dostupné žádné plány.",
        currentBadge: "Aktuální",
        manage: "Spravovat",
        switchPlan: "Přepnout",
        tabSubscription: "Předplatné",
        tabTopUp: "Dobití",
        packOfMonth: "+{pct} % k měsíčnímu limitu",
    },
    uk: {
        subscribeTitle: "Оберіть план",
        subscribeDescription: "Оформіть підписку — безпечна оплата, не виходячи з застосунку.",
        tokensTitle: "Поповнити",
        tokensDescription: "Оберіть пакет — безпечна оплата, не виходячи з застосунку.",
        payWithCard: "Оплатити карткою",
        payWithStars: "Оплатити Telegram Stars",
        loading: "Завантаження…",
        starsWaitingHint: "Завершіть оплату в Telegram — покупка зʼявиться тут за мить.",
        processingHint: "Оплату отримано — активація займе трохи часу.",
        success: "Готово! Покупка активна.",
        error: "Щось пішло не так. Спробуйте ще раз.",
        close: "Закрити",
        emptyCatalog: "Наразі немає доступних планів.",
        currentBadge: "Поточний",
        manage: "Керувати",
        switchPlan: "Перейти",
        tabSubscription: "Підписка",
        tabTopUp: "Поповнення",
        packOfMonth: "+{pct}% до ліміту місяця",
    },
    de: {
        subscribeTitle: "Plan wählen",
        subscribeDescription: "Jetzt abonnieren — sicher bezahlen, ohne die App zu verlassen.",
        tokensTitle: "Aufladen",
        tokensDescription: "Paket wählen — sicher bezahlen, ohne die App zu verlassen.",
        payWithCard: "Mit Karte zahlen",
        payWithStars: "Mit Telegram Stars zahlen",
        loading: "Wird geladen…",
        starsWaitingHint: "Zahlung in Telegram abschließen — der Kauf erscheint gleich hier.",
        processingHint: "Zahlung erhalten — die Aktivierung dauert einen Moment.",
        success: "Fertig! Dein Kauf ist aktiv.",
        error: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
        close: "Schließen",
        emptyCatalog: "Derzeit sind keine Pläne verfügbar.",
        currentBadge: "Aktuell",
        manage: "Verwalten",
        switchPlan: "Wechseln",
        tabSubscription: "Abo",
        tabTopUp: "Aufladen",
        packOfMonth: "+{pct} % zum Monatskontingent",
    },
    fr: {
        subscribeTitle: "Choisissez une formule",
        subscribeDescription: "Abonnez-vous — paiement sécurisé sans quitter l’application.",
        tokensTitle: "Recharger",
        tokensDescription: "Choisissez un pack — paiement sécurisé sans quitter l’application.",
        payWithCard: "Payer par carte",
        payWithStars: "Payer en Telegram Stars",
        loading: "Chargement…",
        starsWaitingHint: "Terminez le paiement dans Telegram — votre achat apparaîtra ici sous peu.",
        processingHint: "Paiement reçu — l’activation prend un instant.",
        success: "Terminé ! Votre achat est actif.",
        error: "Une erreur s’est produite. Veuillez réessayer.",
        close: "Fermer",
        emptyCatalog: "Aucune formule disponible pour le moment.",
        currentBadge: "Actuel",
        manage: "Gérer",
        switchPlan: "Changer",
        tabSubscription: "Abonnement",
        tabTopUp: "Recharge",
        packOfMonth: "+{pct} % du quota du mois",
    },
};
/** The fleet's chosen UI locale from the parent-domain `owlcat-locale` cookie —
 *  the SITE language, which can differ from the browser's navigator.language. Used
 *  as the loc fallback so the widget matches the app's language, not the browser. */
function readLocaleCookie() {
    if (typeof document === "undefined")
        return null;
    const m = document.cookie.match(/(?:^|;\s*)owlcat-locale=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
}
/** Merge English defaults ← locale built-ins ← caller overrides. */
function resolveLabels(loc, labels) {
    const lang = (loc ?? "").toLowerCase().split("-")[0];
    return { ...DEFAULT_LABELS, ...(WIDGET_LOCALE_LABELS[lang] ?? {}), ...labels };
}
// ── stripe.js singleton (one loadStripe per publishable key) ─────────────────────
const stripeCache = new Map();
function stripePromiseFor(pk) {
    let p = stripeCache.get(pk);
    if (!p) {
        p = loadStripe(pk);
        stripeCache.set(pk, p);
    }
    return p;
}
/** Pull a string[] of feature bullets out of loose plan metadata. */
function readFeatures(meta) {
    const f = meta?.features;
    if (Array.isArray(f))
        return f.filter((x) => typeof x === "string");
    return undefined;
}
/** Pull a highlight-ribbon label out of loose plan metadata (`badge` or `popular:true`). */
function readBadge(meta, popularFallback) {
    if (!meta)
        return undefined;
    if (typeof meta.badge === "string" && meta.badge.trim())
        return meta.badge;
    if (meta.popular === true || meta.popular === "true")
        return popularFallback;
    return undefined;
}
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
function PurchaseWidget(props) {
    const { mode, appId, accountsUrl, stripePublishableKey, labels: t, floating, triggerLabel, openEvent, loadItems, createEmbedded, createStarsLink, changePlan, pollComplete, capturePreState, } = props;
    const [mounted, setMounted] = useState(false);
    const [inTma, setInTma] = useState(false);
    // The app the modal is CURRENTLY scoped to — defaults to the mounted appId, but
    // an openSubscribe("otherApp") event re-scopes it (ref so checkout/poll read the
    // live value synchronously, no stale-closure race with the open handler).
    const activeAppId = useRef(appId);
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState(null);
    const [phase, setPhase] = useState("browse");
    const [busy, setBusy] = useState(null);
    const [errorDetail, setErrorDetail] = useState(null); // the failing call's code, shown under t.error
    const [clientSecret, setClientSecret] = useState(null);
    const activeItem = useRef(null);
    const preState = useRef(0);
    // Fleet analytics (analytics-events.ts): `checkout_started` ONCE per sheet open — the
    // intent fact for every app's funnel (the money fact, checkout_completed, is accounts').
    // Wired here so no app re-implements it; consent-gated + prod-only inside useTrack.
    const track = useTrack();
    // The rail of the purchase in flight — reported on checkout_completed (card → stripe, Stars → stars,
    // a plan switch on a card on file → stripe) and the plan the account came from for subscription_changed.
    const lastMethod = useRef("stripe");
    const switchedFrom = useRef(undefined);
    // Detect the TMA context AFTER mount (window-only) — never flash a card path in a TMA.
    // Also advertise this widget as mounted so header chrome can open it in-app (else
    // it falls back to the accounts redirect).
    useEffect(() => {
        setInTma(isTelegramMiniApp());
        setMounted(true);
        const flag = mode === "subscription" ? SUB_FLAG : TOK_FLAG;
        bumpMountFlag(flag, +1);
        return () => bumpMountFlag(flag, -1);
    }, [mode]);
    const resetTransient = useCallback(() => {
        setPhase("browse");
        setBusy(null);
        setClientSecret(null);
        activeItem.current = null;
    }, []);
    // the sibling dialog (plan ↔ top-up) is mounted too: the app sells both, show the tabs
    const [otherMounted, setOtherMounted] = useState(false);
    const doOpen = useCallback(async () => {
        setOtherMounted(mode === "subscription" ? isBuyTokensWidgetMounted() : isSubscribeWidgetMounted());
        setOpen(true);
        resetTransient();
        const inTelegram = isTelegramMiniApp();
        track("checkout_started", {
            kind: mode === "subscription" ? "subscription" : "topup",
            app_id: activeAppId.current,
            // In a TMA the only rail is Stars; in a browser the method is picked later.
            method: inTelegram ? "stars" : "unknown",
        });
        // Always reload — the modal may have been re-scoped to a different app since
        // the last open, so a cached item list could be for the wrong catalog.
        setItems(null);
        try {
            setItems(await loadItems(inTelegram, activeAppId.current));
        }
        catch {
            setItems([]);
        }
    }, [loadItems, resetTransient, track, mode]);
    // Event-driven opener (header button, low-balance nudge, …). The event may
    // carry an appId to re-scope the modal (openSubscribe("habits")); absent → the
    // mounted appId.
    useEffect(() => {
        const openIt = (e) => {
            const detail = e.detail;
            activeAppId.current = detail?.appId ?? appId;
            setWantedTier(detail?.tier ?? null);
            void doOpen();
        };
        window.addEventListener(openEvent, openIt);
        return () => window.removeEventListener(openEvent, openIt);
    }, [openEvent, doOpen, appId]);
    const onOpenChange = useCallback((next) => {
        setOpen(next);
        if (!next) {
            resetTransient();
            setWantedTier(null);
        }
    }, [resetTransient]);
    // the pre-chosen plan (openSubscribe(app, tier)): highlighted + scrolled into view once the list is in
    const [wantedTier, setWantedTier] = useState(null);
    const wantedRef = useCallback((el) => { if (el)
        setTimeout(() => el.scrollIntoView({ block: "center", behavior: "smooth" }), 50); }, []);
    // ── poll the SELF endpoint until the async credit/upsert lands ──
    const startPolling = useCallback(async (item) => {
        setPhase("polling");
        // ~24 tries × 2.5s ≈ 60s.
        for (let i = 0; i < 24; i++) {
            try {
                if (await pollComplete(item, preState.current, activeAppId.current)) {
                    setPhase("success");
                    const kind = mode === "subscription" ? "subscription" : "topup";
                    track("checkout_completed", {
                        kind,
                        app_id: activeAppId.current,
                        method: lastMethod.current,
                        ...(kind === "subscription" ? { plan_id: item.id } : { package_id: item.id }),
                    });
                    if (kind === "subscription" && switchedFrom.current !== undefined) {
                        track("subscription_changed", { app_id: activeAppId.current, from_plan_id: switchedFrom.current, to_plan_id: item.id });
                        switchedFrom.current = undefined;
                    }
                    if (typeof window !== "undefined") {
                        window.dispatchEvent(new CustomEvent(PURCHASE_COMPLETE_EVENT, { detail: { appId: activeAppId.current, mode } }));
                    }
                    return;
                }
            }
            catch {
                /* keep polling — transient network */
            }
            await sleep(2500);
        }
        setPhase("processing"); // graceful timeout — credit may still land
    }, [pollComplete]);
    // ── Stripe card (browser only) → embedded checkout ──
    const buyCard = useCallback(async (item) => {
        if (inTma)
            return; // COMPLIANCE: never a card path inside a TMA
        if (!stripePublishableKey) {
            setPhase("error");
            return;
        }
        setBusy(item.id + ":card");
        lastMethod.current = "stripe";
        try {
            preState.current = await capturePreState(activeAppId.current);
            const secret = await createEmbedded(item, activeAppId.current);
            if (!secret) {
                setPhase("error");
                setBusy(null);
                return;
            }
            activeItem.current = item;
            setClientSecret(secret);
            setPhase("embedded");
        }
        catch (e) {
            setErrorDetail(e instanceof Error ? e.message : String(e));
            setPhase("error");
        }
        finally {
            setBusy(null);
        }
    }, [inTma, stripePublishableKey, capturePreState, createEmbedded]);
    // Fires when the embedded payment completes (redirect_on_completion:'never').
    const onEmbeddedComplete = useCallback(() => {
        const item = activeItem.current;
        if (item)
            void startPolling(item);
    }, [startPolling]);
    // ── Telegram Stars (native in TMA, new tab in browser) ──
    const buyStars = useCallback(async (item) => {
        setBusy(item.id + ":stars");
        lastMethod.current = "stars";
        try {
            preState.current = await capturePreState(activeAppId.current);
            const link = await createStarsLink(item, activeAppId.current);
            activeItem.current = item;
            if (inTma) {
                const opened = openTelegramInvoice(link, (status) => {
                    setBusy(null);
                    if (status === "paid")
                        void startPolling(item);
                });
                if (!opened) {
                    window.open(link, "_blank", "noopener,noreferrer");
                    setBusy(null);
                    void startPolling(item);
                }
            }
            else {
                window.open(link, "_blank", "noopener,noreferrer");
                setBusy(null);
                void startPolling(item);
            }
        }
        catch (e) {
            setErrorDetail(e instanceof Error ? e.message : String(e));
            setPhase("error");
            setBusy(null);
        }
    }, [inTma, capturePreState, createStarsLink, startPolling]);
    // ── Switch plan (existing subscription, card on file → no new payment UI) ──
    const buySwitch = useCallback(async (item) => {
        if (!changePlan)
            return;
        setBusy(item.id + ":switch");
        lastMethod.current = "stripe";
        switchedFrom.current = items?.find((i) => i.current)?.id ?? "";
        try {
            preState.current = await capturePreState(activeAppId.current);
            const res = await changePlan(item, activeAppId.current);
            setBusy(null);
            if (res.ok) {
                activeItem.current = item;
                void startPolling(item);
            }
            else {
                setPhase("error");
            }
        }
        catch (e) {
            setErrorDetail(e instanceof Error ? e.message : String(e));
            setPhase("error");
            setBusy(null);
        }
    }, [changePlan, capturePreState, startPolling]);
    const title = mode === "subscription" ? t.subscribeTitle : t.tokensTitle;
    const description = mode === "subscription" ? t.subscribeDescription : t.tokensDescription;
    return (_jsxs(_Fragment, { children: [floating && mounted && (_jsx(Button, { type: "button", onClick: () => void doOpen(), "data-testid": "purchase-trigger", className: "fixed bottom-4 right-20 z-40 shadow-lg", children: triggerLabel ?? title })), _jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: _jsxs(DialogContent, { className: "sm:max-w-lg", "data-testid": "purchase-dialog", children: [_jsxs(DialogHeader, { children: [otherMounted && phase === "browse" ? (_jsx("div", { role: "tablist", className: "mb-1 flex gap-1 rounded-lg bg-muted p-1 text-sm", "data-testid": "purchase-tabs", children: ["subscription", "tokens"].map((m) => (_jsx("button", { type: "button", role: "tab", "aria-selected": m === mode, "data-testid": `purchase-tab-${m}`, className: m === mode
                                            ? "flex-1 rounded-md bg-background px-3 py-1.5 font-medium text-foreground shadow-sm"
                                            : "flex-1 rounded-md px-3 py-1.5 text-muted-foreground hover:text-foreground", onClick: () => {
                                            if (m === mode)
                                                return;
                                            const forApp = activeAppId.current;
                                            onOpenChange(false);
                                            window.dispatchEvent(new CustomEvent(m === "subscription" ? SUBSCRIBE_OPEN_EVENT : BUY_TOKENS_OPEN_EVENT, { detail: { appId: forApp } }));
                                        }, children: m === "subscription" ? t.tabSubscription : t.tabTopUp }, m))) })) : null, _jsx(DialogTitle, { children: title }), _jsx(DialogDescription, { children: description })] }), phase === "embedded" && clientSecret && stripePublishableKey ? (_jsx("div", { "data-testid": "embedded-checkout", className: "max-h-[70vh] overflow-y-auto", children: _jsx(EmbeddedCheckoutProvider, { stripe: stripePromiseFor(stripePublishableKey), options: { clientSecret, onComplete: onEmbeddedComplete }, children: _jsx(EmbeddedCheckout, {}) }) })) : phase === "polling" ? (_jsx("p", { className: "py-6 text-center text-sm text-muted-foreground", "data-testid": "purchase-polling", children: t.loading })) : phase === "success" ? (_jsxs("div", { className: "flex flex-col gap-4 py-4", children: [_jsx("p", { className: "text-sm text-foreground", "data-testid": "purchase-success", children: t.success }), _jsx(Button, { onClick: () => onOpenChange(false), children: t.close })] })) : phase === "processing" ? (_jsxs("div", { className: "flex flex-col gap-4 py-4", children: [_jsx("p", { className: "text-sm text-muted-foreground", "data-testid": "purchase-processing", children: t.processingHint }), _jsx(Button, { variant: "outline", onClick: () => onOpenChange(false), children: t.close })] })) : phase === "error" ? (_jsxs("div", { className: "flex flex-col gap-4 py-4", children: [_jsxs("p", { className: "text-sm text-destructive", role: "alert", "data-testid": "purchase-error", children: [t.error, errorDetail && _jsx("span", { className: "mt-1 block font-mono text-xs opacity-80", "data-testid": "purchase-error-detail", children: errorDetail })] }), _jsx(Button, { variant: "outline", onClick: resetTransient, children: t.close })] })) : items === null ? (_jsx("p", { className: "py-6 text-center text-sm text-muted-foreground", children: t.loading })) : items.length === 0 ? (_jsx("p", { className: "py-6 text-center text-sm text-muted-foreground", "data-testid": "purchase-empty", children: t.emptyCatalog })) : (_jsxs("div", { className: mode === "subscription"
                                ? "flex flex-col gap-3"
                                : "grid grid-cols-1 gap-3 sm:grid-cols-2", "data-testid": "purchase-items", children: [items.map((item) => {
                                    // Pure CTA decision (unit-tested in purchase-logic): current tier →
                                    // no Buy button (compliance-safe: `card` is already false in a TMA).
                                    const cta = planCtaState({
                                        isCurrent: item.current,
                                        card: !inTma && item.card,
                                        stars: item.stars,
                                    });
                                    const wanted = !!wantedTier && item.tier === wantedTier && !item.current;
                                    return (_jsxs("div", { ref: wanted ? wantedRef : undefined, "data-testid": `purchase-item-${item.id}`, "data-current": item.current ? "true" : undefined, "data-wanted": wanted ? "true" : undefined, className: `relative flex flex-col gap-3 rounded-xl border p-4 ${item.current ? "border-primary ring-1 ring-primary" : wanted ? "border-amber-400 ring-2 ring-amber-300" : "border-border"}`, children: [(item.current || item.badge) && (_jsx("span", { className: "absolute -top-2 right-3 rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold text-primary-foreground", "data-testid": item.current ? `current-badge-${item.id}` : `badge-${item.id}`, children: item.current ? t.currentBadge : item.badge })), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("p", { className: "text-sm font-semibold text-foreground", children: item.title }), _jsxs("p", { className: "flex items-baseline gap-1", children: [_jsx("span", { className: "text-2xl font-bold text-foreground", children: item.priceLabel }), item.interval && (_jsxs("span", { className: "text-sm text-muted-foreground", children: ["/", item.interval] }))] }), item.subtitle && !item.interval && (_jsx("p", { className: "text-xs text-muted-foreground", children: item.subtitle }))] }), item.features && item.features.length > 0 && (_jsx("ul", { className: "flex flex-col gap-1.5", "data-testid": `features-${item.id}`, children: item.features.map((f, i) => (_jsxs("li", { className: "flex items-start gap-2 text-sm text-muted-foreground", children: [_jsx("span", { "aria-hidden": true, className: "mt-0.5 text-primary", children: "\u2713" }), _jsx("span", { children: f })] }, i))) })), _jsx("div", { className: "mt-auto flex flex-col gap-2 sm:flex-row", children: cta === "current" ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "inline-flex flex-1 min-w-0 items-center justify-center rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-medium text-primary", "data-testid": `current-${item.id}`, children: t.currentBadge }), item.manageHref && (_jsx(Button, { asChild: true, variant: "outline", className: "flex-1 min-w-0", children: _jsx("a", { href: item.manageHref, "data-testid": `manage-${item.id}`, className: "truncate", children: t.manage }) }))] })) : item.switchable ? (_jsx(Button, { size: "sm", variant: "outline", className: "w-full", disabled: busy !== null, onClick: () => void buySwitch(item), "data-testid": `switch-${item.id}`, title: `${t.switchPlan} — ${item.priceLabel}`, children: t.switchPlan })) : (_jsxs(_Fragment, { children: [!inTma && item.card && (_jsxs(Button, { size: "sm", className: "flex-1", disabled: busy !== null, onClick: () => void buyCard(item), "data-testid": `buy-card-${item.id}`, "aria-label": `${t.payWithCard} — ${item.priceLabel}`, title: t.payWithCard, children: [_jsx("span", { "aria-hidden": true, children: "\uD83D\uDCB3" }), "\u00A0", item.priceLabel] })), item.stars && (_jsxs(Button, { size: "sm", className: "flex-1", variant: inTma ? undefined : "outline", disabled: busy !== null, onClick: () => void buyStars(item), "data-testid": `buy-stars-${item.id}`, "aria-label": `${t.payWithStars}${item.starsAmount != null ? ` — ${item.starsAmount}` : ""}`, title: t.payWithStars, children: [_jsx("span", { "aria-hidden": true, children: "\u2B50" }), "\u00A0", item.starsAmount != null ? item.starsAmount : t.payWithStars] }))] })) })] }, item.id));
                                }), mode !== "subscription" && (_jsx("p", { className: "text-xs text-muted-foreground sm:col-span-2", children: t.starsWaitingHint }))] }))] }) })] }));
}
// ── shared fetch helpers ───────────────────────────────────────────────────────
function base(accountsUrl) {
    return (accountsUrl ?? "").trim().replace(/\/+$/, "");
}
async function postJson(url, body) {
    const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        // surface the server's error code (unknown plan / no Stars price / unauthorized…) — the dialog
        // shows it under the generic line so a failed purchase can be diagnosed from a screenshot
        const body = (await res.json().catch(() => null));
        throw new Error(`HTTP ${res.status}${body?.error ? ` · ${body.error}` : ""}${body?.message ? ` · ${body.message}` : ""}`);
    }
    return (await res.json());
}
export function SubscribeWidget(props) {
    const { appId, accountsUrl, stripePublishableKey, locale, labels, floating = false, triggerLabel } = props;
    const b = base(accountsUrl);
    const loc = locale ?? readLocaleCookie() ?? (typeof navigator !== "undefined" ? navigator.language : null);
    const t = resolveLabels(loc, labels);
    const loadItems = useCallback(async (inTma, scopedAppId) => {
        // Fetch the catalog + the buyer's live entitlements in parallel so each tier can
        // be flagged "current" (anon → 401 → no current tier, everything purchasable).
        const [plansRes, entRes] = await Promise.all([
            // the catalog comes back in the site language (accounts localizes names/features)
            fetch(`${b}/billing/plans?app=${encodeURIComponent(scopedAppId)}&lang=${encodeURIComponent((loc ?? "en").slice(0, 2))}`, {
                credentials: "include",
                headers: { Accept: "application/json" },
            }),
            fetch(`${b}/me/entitlements?app=${encodeURIComponent(scopedAppId)}`, {
                credentials: "include",
                headers: { Accept: "application/json" },
            }).catch(() => null),
        ]);
        if (!plansRes.ok)
            throw new Error(`HTTP ${plansRes.status}`);
        const data = (await plansRes.json());
        let ent = null;
        if (entRes && entRes.ok) {
            try {
                ent = (await entRes.json());
            }
            catch {
                ent = null;
            }
        }
        // Does the buyer already hold ANY active tier of this app? If so, every
        // OTHER tier is a plan CHANGE (switch), not a fresh purchase.
        const hasActiveTier = (ent?.activeTiers?.length ?? 0) > 0;
        return (data.plans ?? []).map((p) => {
            const cur = pickDisplayCurrency(p.prices, p.base_currency, loc);
            const amount = p.prices[cur] ?? p.prices[p.base_currency];
            const methods = paymentMethods({
                inTma,
                hasStripe: p.has_stripe,
                starsAmount: p.tg_stars_amount,
            });
            // the free tier is what a signed-in buyer without any paid tier is ON — say so
            const current = entitlementHasTier(ent, p.tier) || (!!ent && !hasActiveTier && p.tier === "free");
            return {
                id: p.id,
                title: p.display_name,
                interval: p.interval,
                features: readFeatures(p.metadata),
                badge: readBadge(p.metadata, "Popular"),
                current,
                // "Manage" the current tier lands on the accounts billing surface (the one
                // place subscriptions are changed/cancelled), carrying app for return.
                manageHref: current ? `${b}/billing?app=${encodeURIComponent(scopedAppId)}` : undefined,
                priceLabel: amount != null ? formatPrice(amount, cur, loc) : "",
                card: methods.card,
                stars: methods.stars,
                starsAmount: p.tg_stars_amount,
                tier: p.tier,
                switchable: hasActiveTier && !current,
            };
        });
    }, [b, loc]);
    const changePlan = useCallback(async (item, scopedAppId) => {
        const res = await fetch(`${b}/billing/subscription/change`, {
            method: "POST",
            credentials: "include",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ app: scopedAppId, planId: item.id }),
        });
        const data = (await res.json().catch(() => ({})));
        return { ok: res.ok && data.ok === true, code: data.code };
    }, [b]);
    const createEmbedded = useCallback(async (item, scopedAppId) => {
        const { clientSecret } = await postJson(`${b}/billing/subscribe/embedded`, { planId: item.id, app: scopedAppId });
        return clientSecret ?? null;
    }, [b]);
    const createStarsLink = useCallback(async (item) => {
        // Stars invoice resolves the app from the plan itself (planId), so it needs
        // no appId — the plan row carries its owning app.
        const { link } = await postJson(`${b}/billing/stars-invoice`, {
            kind: "plan",
            planId: item.id,
        });
        return link;
    }, [b]);
    const pollComplete = useCallback(async (item, _pre, scopedAppId) => {
        const res = await fetch(`${b}/me/entitlements?app=${encodeURIComponent(scopedAppId)}`, {
            credentials: "include",
            headers: { Accept: "application/json" },
        });
        if (!res.ok)
            return false;
        const ent = (await res.json());
        return entitlementHasTier(ent, item.tier ?? "");
    }, [b]);
    const capturePreState = useCallback(async (_scopedAppId) => 0, []);
    return (_jsx(PurchaseWidget, { mode: "subscription", appId: appId, accountsUrl: accountsUrl, stripePublishableKey: stripePublishableKey, labels: t, locale: loc, floating: floating, triggerLabel: triggerLabel, openEvent: SUBSCRIBE_OPEN_EVENT, loadItems: loadItems, createEmbedded: createEmbedded, createStarsLink: createStarsLink, changePlan: changePlan, pollComplete: pollComplete, capturePreState: capturePreState }));
}
export function BuyTokensWidget(props) {
    const { appId, accountsUrl, stripePublishableKey, packages, tokensUnit = "tokens", describeTokens, locale, labels, floating = false, triggerLabel, } = props;
    const packTitle = (tokens) => describeTokens ? describeTokens(tokens) : `${tokens.toLocaleString(loc ?? undefined)} ${tokensUnit}`;
    const b = base(accountsUrl);
    const loc = locale ?? readLocaleCookie() ?? (typeof navigator !== "undefined" ? navigator.language : null);
    const t = resolveLabels(loc, labels);
    const loadItems = useCallback(async (inTma) => {
        // App supplied a catalog → use it verbatim (prices already formatted, like BuyButtons).
        if (packages && packages.length > 0) {
            return packages.map((p) => {
                const methods = paymentMethods({
                    inTma,
                    hasStripe: p.hasStripe !== false,
                    starsAmount: p.hasStars === false ? null : 1,
                });
                return {
                    id: p.id,
                    title: packTitle(p.tokens),
                    priceLabel: p.price,
                    card: methods.card,
                    stars: methods.stars,
                    starsAmount: p.tgStarsAmount ?? null,
                };
            });
        }
        // No catalog prop → fetch the fleet catalog + format prices in the buyer's currency.
        // The app's plans and the buyer's tiers ride along: a pack nobody described is said
        // as its share of the month (the sidebar's % ring), never as a raw token count.
        const lang = (loc ?? "en").slice(0, 2).toLowerCase();
        const [res, plansRes, entRes] = await Promise.all([
            fetch(`${b}/billing/packages?app=${encodeURIComponent(appId)}&lang=${encodeURIComponent(lang)}`, {
                credentials: "include",
                headers: { Accept: "application/json" },
            }),
            describeTokens
                ? null
                : fetch(`${b}/billing/plans?app=${encodeURIComponent(appId)}`, { credentials: "include", headers: { Accept: "application/json" } }).catch(() => null),
            describeTokens
                ? null
                : fetch(`${b}/me/entitlements?app=${encodeURIComponent(appId)}`, { credentials: "include", headers: { Accept: "application/json" } }).catch(() => null),
        ]);
        if (!res.ok)
            throw new Error(`HTTP ${res.status}`);
        const data = (await res.json());
        const plans = plansRes && plansRes.ok ? ((await plansRes.json().catch(() => ({}))).plans ?? []) : [];
        const tiers = entRes && entRes.ok ? ((await entRes.json().catch(() => ({}))).activeTiers ?? []) : [];
        // the app's own words first (it counts content exactly), then the admin's, then the share of the month
        const describe = (p) => {
            if (describeTokens)
                return describeTokens(p.tokens);
            if (p.label)
                return p.label;
            const pct = packShareOfMonth(p.tokens, plans, tiers);
            return pct !== null ? t.packOfMonth.replace("{pct}", String(pct)) : packTitle(p.tokens);
        };
        return (data.packages ?? []).map((p) => {
            const cur = pickDisplayCurrency(p.prices, p.base_currency, loc);
            const amount = p.prices[cur] ?? p.prices[p.base_currency];
            const methods = paymentMethods({
                inTma,
                hasStripe: p.has_stripe,
                starsAmount: p.tg_stars_amount,
            });
            return {
                id: p.id,
                title: describe(p),
                priceLabel: amount != null ? formatPrice(amount, cur, loc) : "",
                card: methods.card,
                stars: methods.stars,
                starsAmount: p.tg_stars_amount,
            };
        });
    }, [packages, loc, describeTokens, tokensUnit, b, appId, t.packOfMonth]);
    // The target app rides on every rail so accounts grants the pack into THAT
    // app's allowance pool (checkout refuses a pack without an app).
    const createEmbedded = useCallback(async (item, forApp) => {
        const { clientSecret } = await postJson(`${b}/billing/checkout/embedded`, { packageId: item.id, app: forApp });
        return clientSecret ?? null;
    }, [b]);
    const createStarsLink = useCallback(async (item, forApp) => {
        const { link } = await postJson(`${b}/billing/stars-invoice`, {
            kind: "package",
            packageId: item.id,
            app: forApp,
        });
        return link;
    }, [b]);
    // Per-app allowance `available` — the poll target that proves the grant landed.
    const readBalance = useCallback(async (forApp) => {
        try {
            const res = await fetch(`${b}/me/balance?app=${encodeURIComponent(forApp)}`, {
                credentials: "include",
                headers: { Accept: "application/json" },
            });
            if (!res.ok)
                return 0;
            const data = (await res.json());
            return typeof data.balance === "number" ? data.balance : 0;
        }
        catch {
            return 0;
        }
    }, [b]);
    const pollComplete = useCallback(async (_item, before, forApp) => balanceIncreased(before, await readBalance(forApp)), [readBalance]);
    return (_jsx(PurchaseWidget, { mode: "tokens", appId: appId, accountsUrl: accountsUrl, stripePublishableKey: stripePublishableKey, labels: t, locale: loc, floating: floating, triggerLabel: triggerLabel, openEvent: BUY_TOKENS_OPEN_EVENT, loadItems: loadItems, createEmbedded: createEmbedded, createStarsLink: createStarsLink, pollComplete: pollComplete, capturePreState: readBalance }));
}
